import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { subscriptionPlans } from '../data/features';
import { Reveal, Stagger, staggerItem } from './MotionPrimitives';

/**
 * Full subscription section - all plan features visible (previous content),
 * exceptional dark-theme presentation.
 */
export default function Pricing({ onGetStarted }) {
  const [billing, setBilling] = useState('monthly');

  return (
    <section id="subscriptions" className="section-pad relative overflow-hidden bg-ink">
      <div className="pointer-events-none absolute -left-20 top-20 h-72 w-72 rounded-full bg-accent/20 blur-[100px]" />
      <div className="pointer-events-none absolute -right-16 bottom-10 h-80 w-80 rounded-full bg-violet-500/15 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="pill-dark">Subscriptions</span>
          <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
            Plans that fit <span className="grad-text">how you sell</span>
          </h2>
          <p className="mt-4 text-mute">
            Every plan inclusion is listed below. Pay for 6 months and save{' '}
            <span className="text-white">20%</span>. Need multi-branch? Ask for Custom.
          </p>
        </Reveal>

        <Reveal className="mt-8 flex justify-center">
          <div className="inline-flex rounded-full border border-white/15 bg-black/80 p-1 shadow-glow backdrop-blur">
            {[
              { id: 'monthly', label: 'Monthly' },
              { id: 'semiAnnual', label: 'Semi-annual · 20% off' },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setBilling(opt.id)}
                className={`relative rounded-full px-5 py-2.5 text-sm font-semibold transition ${
                  billing === opt.id ? 'text-black' : 'text-mute hover:text-white'
                }`}
              >
                {billing === opt.id && (
                  <motion.span
                    layoutId="billing-pill"
                    className="absolute inset-0 rounded-full bg-white"
                    transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                  />
                )}
                <span className="relative z-10">{opt.label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <Stagger className="mt-12 grid gap-6 lg:grid-cols-2">
          {subscriptionPlans.map((plan) => {
            const price = plan.prices[billing];

            return (
              <motion.div
                key={plan.name}
                variants={staggerItem}
                whileHover={{ y: -4 }}
                className={`relative flex flex-col overflow-hidden rounded-3xl border p-6 sm:p-8 ${
                  plan.featured
                    ? 'border-accent/50 bg-gradient-to-b from-accent/15 via-card to-card shadow-glow'
                    : 'border-white/10 bg-card'
                }`}
              >
                {plan.featured && (
                  <div className="absolute right-0 top-0 rounded-bl-2xl bg-grad-btn px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white">
                    Most popular
                  </div>
                )}

                <div className="pr-16">
                  <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={billing + plan.name}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="mt-3 text-3xl font-extrabold tracking-tight text-white"
                    >
                      {price.price}
                    </motion.p>
                  </AnimatePresence>
                  {(price.note || price.discount) && (
                    <p className="mt-1 text-sm text-faint">
                      {price.note}
                      {price.discount ? ` · ${price.discount}` : ''}
                    </p>
                  )}
                </div>

                <ul className="mt-8 grid flex-1 gap-2.5 sm:grid-cols-2">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className="flex gap-2 rounded-xl border border-white/5 bg-black/30 px-3 py-2 text-sm text-mute"
                    >
                      <span className="mt-0.5 shrink-0 text-emerald-400">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={onGetStarted}
                  className={`mt-6 w-full rounded-full py-3.5 text-sm font-semibold transition ${
                    plan.featured ? 'btn-grad' : 'btn-ghost'
                  }`}
                >
                  {plan.name === 'Custom Plan' ? 'Talk to us' : 'Choose plan'}
                </button>
              </motion.div>
            );
          })}
        </Stagger>

        <Reveal className="mt-10 text-center text-sm text-faint">
          Need help picking a plan for your branch count?{' '}
          <a href="#contact" className="font-semibold text-accent hover:text-accent-soft">
            Contact sales
          </a>
        </Reveal>
      </div>
    </section>
  );
}
